import React, { FC, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import routes from '../../constants/routes';
import { StockCardProps } from '.';
import StockModelCardHeader from './shared/StockCardHeader';
import StockModelCardCaption from './shared/StockCardCaption';
import StockModelCardOptions from './shared/StockCardOptions';
import StockModelCardDealerInfo from './shared/StockCardDealerInfo';
import useCustomRouter from '@hooks/useCustomRouter';
import { IPriceStock, StockFormatedPriceType } from '../../services/stock/models/stockItem.price.model';
import { ICalculateSummaryRequestData } from '../../services/calculateSummary/calculateSummary.types';
import StockService from '../../services/stock/stock.service';
import getDealerNameAndCity from './utils/getDealerNameAndCity';
import UIDuck from '../../redux/commonDucks/ui.duck';
import useTranslations from '@hooks/useTranslations';
import { getFinanceDetailText, getFinanceDetailView, getMonthlyPriceObjByType } from '@utils/Price.utils';
import { useSummaryDataPromise } from '../OfferListItem/hooks/useSummaryDataPromise';
import { IVehicleTransformed } from '../../services/stock/models/stockItems.service.model';
import PriceBox from '../PriceBox/Configurable';
import { useGTM } from '@hooks/useGTM';
import { useCarStockJourneyCheck } from '@hooks/useCarStockJourneyCheck';
import { usePrices } from '@hooks/usePriceCalculator';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { DeliveryDate, PriceBoxContainer } from './StockCardStyled';
import { buildParameterizedPathname } from '@utils/url.utils';
import { CarJourneyType, CASH, FINANCE } from '../../services';
import CarDetailsDuck from '../../redux/carDetails/carDetails.duck';
import { Icons } from '../Icon';
import { IconTemplate } from '../Icon/IconTemplate';
import { formatLeadTime } from '@utils/Date.utils';
import { createImageAltResolver, extractViewFromImageUrl, useExteriorPreviewImage } from '@utils/Image.utils';
import { ICarImage } from '../../interfaces/Car';
import styled from 'styled-components';
import { css } from '../../styles/theme';
import { Col } from '../Grid';
import { rem } from 'polished';
import { Redux } from 'src/redux/redux.interface';
import FilterDuck from '../../redux/filters/filter.duck';
import { getModelTitle } from '../../services/carList/utils/Car.utils';
import { FEATURES_LIST } from '../../context/featureSwitchApp';
import { useUIDuck } from '@hooks/useUIDuck';
import { BRAND, isBrandAC, isBrandAP, isBrandDS, isBrandOV, isMarketGB, ModalVersion } from '../../constants/main';
import { EContextPage, EType, findPromoByPageAndType } from '@utils/Promo.utils';
import { IOffer } from '../../interfaces/IOfferItem';
import { useFinanceWidgetDuck } from '@hooks/useFinanceWidgetDuck';
import { useRedirection } from '@hooks/useRedirection';
import NeedHandSection from '@components/OfferListItem/NeedHandSection';
import { useFeatureSwitch } from '@hooks/useFeatureSwitch';
import useSessionChangeListener from '@hooks/useSessionChangeListener';

const GEARBOX_SEARCH_KEY = 'type';

const StyledCol = styled(Col)`
    margin-bottom: ${rem(15)};
    ${({ isStockSlice }) =>
        isStockSlice &&
        css`
            flex-basis: 100% !important;
        `}
`;
const OFFER_ELEMENT_CLASS = 'offer-item';

export const StockCardTemplate: FC<StockCardProps> = ({
    className,
    paymentJourney,
    isNeedHandItem,
    vehicle,
    isStockSlice,
    index,
    dealerLocationHeight,
}) => {
    const dispatch = useDispatch();
    const { t } = useTranslations();
    const { isStockJourney } = useCarStockJourneyCheck();
    const { pushUserAction, pushProductClick } = useGTM();
    const { isLoadingNow } = useUIDuck();
    const { handleContinueButtonChange } = useSessionChangeListener();
    const router = useCustomRouter();
    const { redirectToBasket } = useRedirection();
    const { globalTexts, pricesList } = useFinanceWidgetDuck();
    const geoLocation = useSelector((state: Redux) => FilterDuck.getGeoLocation(state));
    const resolveImageAlt = createImageAltResolver(BRAND, t);

    const price = vehicle.prices.find((price: IPriceStock) => price.type === StockFormatedPriceType.EMPLOYEE);
    const monthlyPrice = price.monthlyPrices[0];

    const { cash, finance, catalog } = usePrices(vehicle, true, null, false);
    const { totalPrice: baseTotalPrice } = cash;
    const {
        totalPrice: baseMonthlyPrice,
        financeProperties: { deposit, apr, hasMonthlyPrices },
    } = finance;
    const { totalPrice: catalogPrice } = catalog;
    const engineInfo = [
        vehicle.engine.description,
        vehicle.gearbox.specs.find(({ key }) => key === GEARBOX_SEARCH_KEY)?.label.toUpperCase(),
        vehicle.fuel,
    ];

    const shouldIncludeBodyStyleDescription = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_STOCK_SHOW_GR_BODY_STYLE_IN_CAR_TITLE
    );
    const { isStockSummaryEnabled } = useFeatureSwitch();

    const depositPrice = deposit;
    const { dealerName, dealerCity } = getDealerNameAndCity(vehicle.dealers[0] as any);

    const distance = vehicle?.dealers?.[0]?.distance;
    const dealerDistance = distance ? Number(distance) : null;

    const title = isStockSlice ? getModelTitle(vehicle, shouldIncludeBodyStyleDescription) : vehicle.specPack.title;

    const vehicleOptions = vehicle.options?.map((option) => option.title) ?? [];
    const image = useExteriorPreviewImage(vehicle.images as ICarImage[], 'list');

    const financeDetailView = getFinanceDetailView(vehicle.prices, StockFormatedPriceType.EMPLOYEE);
    const financeDetailText = getFinanceDetailText(
        vehicle?.prices,
        StockFormatedPriceType.EMPLOYEE,
        globalTexts,
        pricesList,
        vehicle.externalId
    );
    const simulateNoCarAvailableOnStock = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_SIMULATE_NO_CAR_AVAILABLE_ON_STOCK
    );
    const isLocalizationEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_FORCE_LOCALIZATION_ENABLED);
    const isPromoEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_PROMO_ENABLED);
    const isMarketingStickerEnabled =
        isPromoEnabled &&
        process.browser &&
        findPromoByPageAndType(
            vehicle?.offers as unknown as (IOffer & { type: EType })[],
            EContextPage.trim,
            isStockJourney
        );
    const handleError = useCallback(
        (vehicle: IVehicleTransformed) => {
            const { model, bodyStyle, nameplateBodyStyleSlug } = vehicle;
            const modelUrl = buildParameterizedPathname(routes.SELECTOR, CarJourneyType.STOCK, {
                nameplateBodyStyleSlug: nameplateBodyStyleSlug,
                modelId: model.id,
                bodyStyleId: bodyStyle.id,
            });

            dispatch(
                UIDuck.openModal({
                    data: {
                        modelName: model.title,
                        modelUrl: modelUrl,
                    },
                    modalType: UIDuck.MODAL_TYPES.STOCK_CAR_UNAVAILABLE,
                    modalVersion: ModalVersion.v2,
                    modalProperties: {
                        centerModeOnDesktop: true,
                    },
                })
            );
        },
        [dispatch]
    );
    const monthlyPriceObj = getMonthlyPriceObjByType(vehicle.prices, StockFormatedPriceType.EMPLOYEE);
    const amountRounding = monthlyPriceObj?.amountRounding;

    const handleRedirect = useCallback(async () => {
        dispatch(UIDuck.setIsLoadingNow(true));
        try {
            if (isLocalizationEnabled && !geoLocation && !router?.query?.idsitegeo) {
                const pathname = buildParameterizedPathname(routes.SELECTOR, CarJourneyType.STOCK, {
                    nameplateBodyStyleSlug: vehicle?.nameplateBodyStyleSlug,
                    modelId: vehicle?.model.id,
                    bodyStyleId: vehicle?.bodyStyle.id,
                });

                const fuel = vehicle?.fuel;
                const specPackDesc = vehicle?.specPack.title;
                const gradesAndEnergiesFilter = `?grades=${specPackDesc}&energies=${fuel}`;

                dispatch(UIDuck.setIsLoadingNow(false));

                return router.push(`${pathname}${gradesAndEnergiesFilter}`);
            }

            const continueToBasketPage = async () => {
                const { deal, success } = await StockService.createStockDeal(
                    vehicle.orders[0].uuid,
                    monthlyPriceObj ? FINANCE : CASH,
                    geoLocation
                );

                if (success && !simulateNoCarAvailableOnStock) {
                    isStockSummaryEnabled ? router.push(routes.SUMMARY) : router.push(routes.BASKET);
                    dispatch(CarDetailsDuck.setCurrentDealAndCarDetail(deal));
                } else {
                    handleError(vehicle);
                }
            };

            const hasActiveDeal = await handleContinueButtonChange(continueToBasketPage);

            if (hasActiveDeal) return;
            else await continueToBasketPage();
        } catch (error: any) {
            handleError(vehicle);
        }

        dispatch(UIDuck.setIsLoadingNow(false));

        pushUserAction({
            action: 'LoadMore',
            label: 'Display More vehicles',
        });
        pushProductClick('stockTrim', vehicle);
    }, [vehicle.orders[0].uuid, Boolean(monthlyPriceObj), handleError, geoLocation]);

    const params: ICalculateSummaryRequestData = {
        carConfigId: vehicle.externalId,
        carConfiguration: vehicle,
    };

    const { processCalculateSummaryDataPromise } = useSummaryDataPromise(params);
    // APP-22942
    // useFinancePricesList();

    return (
        <StyledCol
            className={`${OFFER_ELEMENT_CLASS} offer-enabled`}
            xs={12}
            sm={6}
            md={6}
            lg={4}
            isStockSlice={isStockSlice}
        >
            <div className={className}>
                {isNeedHandItem ? (
                    <NeedHandSection />
                ) : (
                    <>
                        <StockModelCardHeader
                            isMarketingStickerEnabled={isMarketingStickerEnabled}
                            isPromoEnabled={isPromoEnabled}
                            numCar={vehicle?.orders?.[0]?.numCar}
                            title={title}
                            engineInfo={engineInfo}
                            isStockSlice={isStockSlice}
                            specPackTitle={vehicle.specPack.title}
                            hasPromoCode={(vehicle as { hasPromoCode?: boolean }).hasPromoCode}
                        />

                        <PriceBoxContainer
                            isStockJourney={isStockJourney}
                            withoutSidePadding={isBrandDS || isBrandAP || isBrandAC || (isMarketGB && isBrandOV)}
                        >
                            <PriceBox
                                basePrice={baseTotalPrice}
                                monthlyPrice={baseMonthlyPrice}
                                isStockList
                                isStockSlice={isStockSlice}
                                catalogPrice={catalogPrice}
                                depositPrice={depositPrice}
                                amountRounding={amountRounding}
                                calculateSummaryData={processCalculateSummaryDataPromise}
                                promotionalText={vehicle?.extraFields?.promotionalText || vehicle?.promotionalText}
                                itemId={vehicle.id}
                                financeDetailView={financeDetailView}
                                financeDetailText={financeDetailText}
                                apr={apr}
                                hasMonthlyPrices={hasMonthlyPrices}
                            />
                        </PriceBoxContainer>

                        <DeliveryDate className="delivery-date">
                            <IconTemplate name={Icons.Clock} width={15} height={15} />
                            <span>{formatLeadTime(vehicle.leadTimeDate)}</span>
                        </DeliveryDate>
                        <StockModelCardCaption
                            isColorAndInteriorDisplayed
                            imageUrl={image}
                            imageAlt={resolveImageAlt(extractViewFromImageUrl(image), vehicle)}
                            color={vehicle.exteriorColour?.title}
                            interior={vehicle.interiorColour?.description}
                        />
                        <StockModelCardOptions
                            index={index}
                            withTooltip
                            id={vehicle.id}
                            optionsList={vehicleOptions}
                            bodyStyle={vehicle.bodyStyle}
                            hasOptions={(vehicle as { hasOptions?: boolean }).hasOptions}
                        />

                        <StockModelCardDealerInfo
                            availabilityDate={vehicle.maxLeadTimeDate}
                            handleRedirect={handleRedirect}
                            paymentJourney={paymentJourney}
                            dealerName={dealerName}
                            dealerLocationHeight={dealerLocationHeight}
                            dealerCity={dealerCity}
                            dealerDistance={dealerDistance}
                            monthlyPrice={monthlyPrice?.amount}
                            basePrice={baseTotalPrice}
                            monthlyDeposit={monthlyPrice?.depositAmount}
                            isSubmitting={isLoadingNow}
                            processCalculateSummaryData={processCalculateSummaryDataPromise}
                            id={vehicle.id}
                        />
                    </>
                )}
            </div>
        </StyledCol>
    );
};

export default StockCardTemplate;
